import {
  ComponentRef,
  Directive,
  ElementRef,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
  ViewContainerRef,
  inject,
  input
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { ThemePalette } from '@angular/material/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Directive({
  selector: 'button[loading]',
  standalone: true
})
export class ButtonLoadingDirective implements OnChanges, OnDestroy {
  readonly #matButton = inject(MatButton);
  readonly #elementRef = inject<ElementRef<HTMLButtonElement>>(ElementRef);
  readonly #viewContainerRef = inject(ViewContainerRef);
  readonly #renderer2 = inject(Renderer2);

  #spinner: ComponentRef<MatProgressSpinner> | null = null;

  /** Whether the host button should show a loading spinner and become disabled. */
  public readonly loading = input(false);

  /** Disabled state to restore when `loading` becomes false. */
  public readonly disabled = input(false);

  /** Extra CSS classes applied to the created spinner element while loading. */
  public readonly loadingStyleClass = input<string>();

  /** Fallback color used for spinner when `spinnerColor` is not provided. */
  public readonly color = input<string>();

  /** Spinner color (preferred over `color`). */
  public readonly spinnerColor = input<ThemePalette>();

  /** Reacts to `loading` input changes by creating/destroying the spinner and toggling disabled state. */
  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes['loading']) {
      return;
    }

    if (changes['loading'].currentValue) {
      this.#renderer2.addClass(this.#hostElement, 'button-loading');
      this.#setDisabled(true);
      this.#createSpinner();
    } else if (!changes['loading'].firstChange) {
      this.#renderer2.removeClass(this.#hostElement, 'button-loading');
      this.#setDisabled(this.disabled());
      this.#destroySpinner();
    }
  }

  /** Ensures dynamically created spinner is destroyed to prevent memory leaks. */
  public ngOnDestroy(): void {
    this.#destroySpinner();
  }

  get #hostElement(): HTMLButtonElement {
    return this.#elementRef.nativeElement;
  }

  #setDisabled(state: boolean): void {
    this.#matButton.disabled = state;
    this.#hostElement.disabled = state;
  }

  #createSpinner(): void {
    if (!this.#spinner) {
      this.#spinner = this.#viewContainerRef.createComponent(MatProgressSpinner);
      this.#spinner.instance.color = this.spinnerColor() ?? this.color() ?? 'accent';
      this.#spinner.instance.diameter = 20;
      this.#spinner.instance.mode = 'indeterminate';
      this.#renderer2.addClass(this.#spinner.instance._elementRef.nativeElement, 'button-spinner');
      const styleClass = this.loadingStyleClass();
      if (styleClass) {
        const classes = styleClass.split(' ');
        classes.forEach((className) => {
          this.#renderer2.addClass(this.#spinner!.instance._elementRef.nativeElement, className);
        });
      }
      this.#renderer2.appendChild(this.#hostElement, this.#spinner.instance._elementRef.nativeElement);
    }
  }

  #destroySpinner(): void {
    if (this.#spinner) {
      this.#spinner.destroy();
      this.#spinner = null;
    }
  }
}
